import React, { useMemo } from 'react';
import { Tooltip as ReactTooltip, Middleware, ITooltip } from 'react-tooltip';
import styled, { CSSProperties } from 'styled-components';
import { addAlpha } from '@algoan/deep-dive/theme';
import { ComponentProps } from '@algoan/deep-dive/model';

export enum TooltipPosition {
  CURSOR = 'CURSOR',
  DEFAULT = 'DEFAULT',
  TOP_START = 'TOP_START',
  TOP_END = 'TOP_END',
}

export interface TooltipCustomProps extends Omit<ITooltip, 'position'>, ComponentProps {
  position?: TooltipPosition;
  width?: CSSProperties['width'];
}

const forcePlacement: Middleware = ({ newPlacement }: { newPlacement: string }) => ({
  name: 'forcePlacement',
  fn({ placement }: { placement: string }) {
    if (placement !== newPlacement) {
      return {
        reset: {
          placement: newPlacement,
        },
      };
    }

    return {};
  },
});

export const Tooltip = ({
  className,
  'data-testid': dataTestId,
  width,
  position,
  ...props
}: TooltipCustomProps): JSX.Element => {
  const positionOverride = useMemo((): Partial<ITooltip> => {
    if (position === TooltipPosition.TOP_END) {
      return {
        middlewares: [
          forcePlacement({
            newPlacement: 'top-end',
          }),
        ],
      };
    } else if (position === TooltipPosition.TOP_START) {
      return {
        middlewares: [
          forcePlacement({
            newPlacement: 'top-start',
          }),
        ],
      };
    } else if (position === TooltipPosition.CURSOR) {
      return {
        float: true,
      };
    }

    return {};
  }, [position]);

  return (
    <S.Container $width={width} className={className} data-testid={dataTestId ?? `tooltip-${props.id}`}>
      <ReactTooltip className="tooltip" place="top" noArrow={true} {...positionOverride} {...props} />
    </S.Container>
  );
};

const S = {
  Container: styled.div<{ $width?: CSSProperties['width'] }>`
    & .tooltip {
      background-color: ${({ theme }) => theme.white};
      border-radius: 4px;
      box-shadow: 0 3px 20px ${({ theme }) => addAlpha(theme.algoanBlue, 0.2)};
      color: ${({ theme }) => theme.middleGrey};
      font-size: 13px;
      font-weight: ${({ theme }) => theme.fontWeight.regular};
      line-height: 16px;
      opacity: 1 !important;
      padding: 16px;
      white-space: break-spaces;
      word-wrap: break-word;
      z-index: 1001;

      &[class*='place-top-end'],
      &[class*='place-top-start'] {
        margin-top: -8px;
      }

      ${({ $width }) => {
        // Required for the transaction's note tooltip
        // It needs a specific width since it depends of the user input
        return $width !== undefined ? 'width: ' + $width + ';' : '';
      }}
    }
  `,
};
